import { Clear as ClearIcon, Done as DoneIcon, MoreHoriz, Star } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import * as routing from '../../../constants/Routing';
import { Project } from '../../../domain/backoffice/project/Project';
import { GeoFence } from '../../../domain/geoFence/GeoFence';
import Text from '../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../react-components/data-table/DataTable';
import { BackofficeClientsProjectActionsMenu } from '../clients/projects/shared/BackofficeClientsProjectActionsMenu';

type GetProjectsTableColumnsProps = {
  defaultProjectId: string;
  navigate: (destinationUrl: string) => void;
  onRefreshContent: () => void;
};

export const getProjectsTableColumns = ({
  defaultProjectId,
  navigate,
  onRefreshContent,
}: GetProjectsTableColumnsProps) => {
  return [
    {
      width: 200,
      height: 25,
      sortable: true,
      accessorName: `name`,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.name" />,
      formatter: ({ doc: project }: { doc: Project }) => {
        const name = `${project.label ?? ``}`;
        const deleted = project.deleted;

        return (
          <Grid container direction="row" alignItems="center">
            {name && name !== `` ? (
              <RoutalTooltip title={name}>
                <Text
                  variant="button"
                  onClick={(e: any) => {
                    navigate(`${routing.BACKOFFICE.CLIENTS.PROJECTS}/details/${project.id}`);
                  }}
                  style={{
                    fontWeight: 500,
                    ...(deleted ? { textDecoration: `line-through` } : {}),
                  }}
                >
                  {name.length > 35 ? `${name.substring(0, 35)}...` : `${name}`}
                </Text>
              </RoutalTooltip>
            ) : (
              <Text
                variant="button"
                onClick={(e: any) => {
                  navigate(`${routing.BACKOFFICE.CLIENTS.PROJECTS}/details/${project.id}`);
                }}
                style={{
                  fontWeight: 500,
                  ...(deleted ? { textDecoration: `line-through` } : {}),
                  color: RoutalPalette.secondary00,
                }}
                intlId="no_name"
              />
            )}
            {defaultProjectId === project.id ? (
              <Star style={{ height: `18px`, marginTop: `-2px`, color: RoutalPalette.warning.medium }} />
            ) : null}
          </Grid>
        );
      },
    },
    {
      width: 60,
      accessorName: `max_vehicles`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: (
        <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.max_vehicles" />
      ),
      formatter: ({ doc: project }: { doc: Project }) => (
        <Text variant="body">{project.resources.max_vehicles_plan}</Text>
      ),
    },
    {
      width: 60,
      accessorName: `max_clients`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.max_clients" />,
      formatter: ({ doc: project }: { doc: Project }) => <Text variant="body">{project.resources.max_clients}</Text>,
    },
    {
      width: 60,
      accessorName: `max_services_plan`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: (
        <Text
          style={{ color: RoutalPalette.primary20 }}
          isBold
          intlId="backoffice.clients.projects.max_services_plan"
        />
      ),
      formatter: ({ doc: project }: { doc: Project }) => <Text variant="body">{project.resources.max_stops_plan}</Text>,
    },
    {
      width: 60,
      accessorName: `geo_fences`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.geo_fences" />,
      formatter: ({ value: geoFences }: { value: GeoFence[] }) => <Text variant="body">{geoFences.length}</Text>,
    },
    {
      width: 80,
      accessorName: `managed`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.is_managed" />,
      formatter: ({ value: managed }: { value: boolean }) => (
        <Text>
          {managed ? (
            <DoneIcon style={{ color: RoutalPalette.success.medium }} />
          ) : (
            <ClearIcon style={{ color: RoutalPalette.error.medium }} />
          )}
        </Text>
      ),
    },
    {
      width: 80,
      accessorName: `anyWhatsAppTemplateActive`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: (
        <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.has_whatsapp" />
      ),
      formatter: ({ doc: project }: { doc: Project }) => {
        const hasAnyWhatsappTemplate = project.customer_whatsapp.countCustomerEnabledWhatsApp() > 0;
        return (
          <Text>
            {hasAnyWhatsappTemplate ? (
              <DoneIcon style={{ color: RoutalPalette.success.medium }} />
            ) : (
              <ClearIcon style={{ color: RoutalPalette.error.medium }} />
            )}
          </Text>
        );
      },
    },
    {
      width: 80,
      accessorName: `traffic`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.traffic" />,
      formatter: ({ doc: project }: { doc: Project }) => (
        <Text>
          {project.resources.traffic ? (
            <DoneIcon style={{ color: RoutalPalette.success.medium }} />
          ) : (
            <ClearIcon style={{ color: RoutalPalette.error.medium }} />
          )}
        </Text>
      ),
    },
    {
      width: 60,
      accessorName: `created_at`,
      align: `center` as AlignmentTypes,
      sortable: true,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.created_at" />,
      formatter: ({ value: createdAt }: { value: Date }) => (
        <RoutalTooltip title={dateFormat(createdAt)}>
          <Text variant="body">{pastDateTime(createdAt)}</Text>
        </RoutalTooltip>
      ),
    },
    {
      width: 120,
      accessorName: `actions`,
      align: `center`,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.actions" />,
      formatter: ({ doc: project }: { doc: Project }) => (
        <Grid container direction="row" wrap="nowrap" justifyContent="flex-end">
          <BackofficeClientsProjectActionsMenu
            project={project}
            type="popover"
            popoverProps={{
              hideArrow: true,
              content: <MoreHoriz />,
              style: { height: `18px`, color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
            }}
            refreshAfterUpdate={() => onRefreshContent()}
          />
        </Grid>
      ),
    } as DataTableColumnProps,
  ];
};
